import React, { useEffect, useMemo, useState } from 'react';

import usePageTitle from '../../hooks/usePageTitle';
import useSelectedResource from '../../hooks/useSelectedResource';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { axiosInstance } from '../../routes/axiosInstance';
import Card, { CardHeader } from '../../components/ui/Card';
import { NoticeBody } from '../../components/notices/NoticesPanel';
import { noticeKind, sortNotices } from '../../components/notices/noticeKinds';
import useDismissedNotices from '../../components/notices/useDismissedNotices';
import {
  RESOURCE_TIMEZONE,
  formatDateTimeInResourceTz,
  getResourceTimezoneLabel,
} from '../../utils/datetime';
import { buildDstNotice, findNextDstTransition } from '../../utils/dstNotice';

const formatWhen = (iso) => `${formatDateTimeInResourceTz(iso)} ${getResourceTimezoneLabel(undefined, new Date(iso))}`;

const isLive = (notice, now) => !notice.ends_at || new Date(notice.ends_at).getTime() > now;

function NoticeRow({ notice, dismissed, onRestore }) {
  const kind = noticeKind(notice.kind);
  let when = null;
  if (!notice.auto) {
    when = notice.ends_at
      ? `${formatWhen(notice.starts_at)} → ${formatWhen(notice.ends_at)}`
      : `Since ${formatWhen(notice.starts_at)}`;
  }
  return (
    <li className="py-3">
      <div className="flex items-start gap-2">
        <span aria-hidden="true" className={`mt-1.5 h-2 w-2 flex-none rounded-full ${kind.dot}`} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
              {kind.label}{notice.auto ? ' · auto' : ''}
            </span>
            {when && <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{when}</span>}
          </div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{notice.title}</div>
          <p className="text-sm text-gray-600 dark:text-gray-300">{notice.summary}</p>
          <NoticeBody notice={notice} summaryOnPhones={false} />
        </div>
        {dismissed && (
          <button
            type="button"
            onClick={() => onRestore(notice)}
            aria-label={`Show on dashboard: ${notice.title}`}
            className="flex-none rounded text-xs font-semibold text-brand-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-brand-400"
          >
            Show on dashboard
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * Every notice addressed to the user: the live ones (dismissed or not, with a
 * way back to the dashboard) and those that ended in the last 90 days.
 */
export default function NoticesPage() {
  usePageTitle('Notices');
  useBreadcrumbs([{ label: 'Notices' }], 'Platform messages, maintenance and clock changes');

  const resource = useSelectedResource();
  const timezone = resource?.timezone || RESOURCE_TIMEZONE;
  const [posted, setPosted] = useState(null); // null while loading
  const { isDismissed, restore } = useDismissedNotices();

  useEffect(() => {
    let stale = false;
    setPosted(null);
    axiosInstance.get('/market/notices/recent', { params: resource?.id ? { resource: resource.id } : {} })
      .then((response) => {
        if (!stale) setPosted(response.data.data || []);
      })
      .catch(() => {
        if (!stale) setPosted([]);
      });
    return () => {
      stale = true;
    };
  }, [resource?.id]);

  const dst = useMemo(() => buildDstNotice(timezone), [timezone]);
  const next = useMemo(() => findNextDstTransition(timezone, new Date(), 400), [timezone]);

  const now = Date.now();
  const live = sortNotices([...(dst ? [dst] : []), ...(posted || []).filter((n) => isLive(n, now))]);
  const ended = (posted || []).filter((n) => !isLive(n, now));

  const list = (notices, empty) => (notices.length === 0 ? (
    <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{empty}</p>
  ) : (
    <ul className="mt-1 divide-y divide-gray-100 dark:divide-gray-700">
      {notices.map((notice) => (
        <NoticeRow key={notice.id} notice={notice} dismissed={isDismissed(notice)} onRestore={restore} />
      ))}
    </ul>
  ));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Current notices"
          description={next
            ? `Next clock change for ${timezone}: ${formatDateTimeInResourceTz(next.at, timezone)}, `
              + `${getResourceTimezoneLabel(timezone, new Date(next.at.getTime() - 1))} → ${getResourceTimezoneLabel(timezone, next.at)}.`
            : `${timezone} has no clock change in the next year.`}
        />
        {posted === null
          ? <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading notices...</p>
          : list(live, 'No current notices.')}
      </Card>
      <Card>
        <CardHeader title="Ended in the last 90 days"/>
        {posted !== null && list(ended, 'None.')}
      </Card>
    </div>
  );
}
